'use client';

import React from 'react';
import { useIntl } from 'react-intl';
import { HStack, VStack, Text, Icon } from '@ui-library';
import { Ban, ChevronRight } from '@ui-library/icons';

import useLocalization from '@/hooks/useLocalization';
import { useCancellationPolicy } from '@/hooks/queries/useCancellationPolicy';
import { ReservationWithRateDetailsDTO } from '@/models';
import { toApiLanguageCode } from '@/providers/i18n/utils';

import { stripHtmlToText } from './cancellationPolicyText';
import { CancellationPolicyErrorBoundary } from './CancellationPolicyErrorBoundary';
import { canRenderCancellationTimeline } from './cancellationTimeline';

type CancellationPolicyRowProps = {
  reservation: ReservationWithRateDetailsDTO;
  isOpen: boolean;
  propertyTimezone?: string;
  onOpen: () => void;
};

/**
 * The "Cancellation policy" row in the reservation drawer. Shows the policy label and a chevron, and
 * opens the V2 timeline sheet — only when the smart policy has resolved deadline dates to show.
 */
const CancellationPolicyRowContent: React.FC<CancellationPolicyRowProps> = ({
  reservation,
  isOpen,
  propertyTimezone,
  onOpen,
}) => {
  const intl = useIntl();
  const { locale } = useLocalization();

  const { policyInformation } = useCancellationPolicy({
    propertyId: reservation.propertyId,
    reservationId: reservation.id,
    languageCode: toApiLanguageCode(locale),
    enabled: isOpen,
  });

  const policyText = policyInformation?.policyText ?? null;

  if (
    !canRenderCancellationTimeline(policyInformation, propertyTimezone) ||
    !policyText
  ) {
    return null;
  }

  const label = stripHtmlToText(policyText.cancellationType?.text);

  return (
    <HStack
      gap={'4'}
      alignItems={'flex-start'}
      w={'full'}
      cursor={'pointer'}
      role={'button'}
      onClick={onOpen}
    >
      <Ban size={20} />
      <VStack alignItems={'flex-start'} gap={0} minW={0} w={'full'}>
        <Text fontWeight={'bold'}>
          {intl.formatMessage({
            id: 'edit_reservation/reservation_details/cancellation_policy_label',
            defaultMessage: 'Cancellation policy',
          })}
        </Text>
        {label && (
          <Text color={'fog.700'} overflowWrap={'anywhere'}>
            {label}
          </Text>
        )}
      </VStack>
      <Icon color={'fog.600'}>
        <ChevronRight size={20} />
      </Icon>
    </HStack>
  );
};

export const CancellationPolicyRow: React.FC<CancellationPolicyRowProps> = (
  props,
) => (
  <CancellationPolicyErrorBoundary>
    <CancellationPolicyRowContent {...props} />
  </CancellationPolicyErrorBoundary>
);
